import { Button } from '@common/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import styles from './styles.module.scss';

interface AuthSession {
    access_token?: string;
    refresh_token?: string;
    token_type?: string;
    user?: {
        id?: string;
        email?: string;
        name?: string;
    };
}

interface SkillItem {
    name: string;
    description?: string;
    download: string;
    version?: string;
}

interface SkillsResponse {
    skills: SkillItem[];
}

const Skill: React.FC = () => {
    const { currentWorkspace } = useAuth();
    const [token, setToken] = useState<string>('');
    const [workspaceId, setWorkspaceId] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [skills, setSkills] = useState<SkillItem[]>([]);
    const [selectedSkill, setSelectedSkill] = useState<SkillItem | null>(null);
    const [isLoadingSkills, setIsLoadingSkills] = useState(true);

    useEffect(() => {
        // 从 localStorage 的 bottom-up-auth-session 获取 token
        const sessionData = localStorage.getItem('bottom-up-auth-session');

        if (sessionData) {
            try {
                const session: AuthSession = JSON.parse(sessionData);
                setToken(session.access_token || '');
            } catch (error) {
                console.error('解析 auth session 失败:', error);
            }
        }

        // 从 currentWorkspace 获取 workspaceId
        if (currentWorkspace?.id) {
            setWorkspaceId(currentWorkspace.id);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        // 当 token 和 workspaceId 都准备好时，设置 isReady
        if (token && workspaceId) {
            setIsReady(true);
        }
    }, [token, workspaceId]);

    useEffect(() => {
        // 从 CDN 获取 skills 列表
        const fetchSkills = async () => {
            try {
                const response = await fetch(`${import.meta.env.VITE_CDN_HOST}/static/skills/skills.json`);
                const data: SkillsResponse = await response.json();
                setSkills(data.skills || []);
            } catch (error) {
                console.error('获取 skills 列表失败:', error);
            } finally {
                setIsLoadingSkills(false);
            }
        };

        fetchSkills();
    }, []);

    const downloadSkill = async () => {
        if (!token || !workspaceId || !selectedSkill) {
            console.error('配置或选中的 skill 未准备好');
            return;
        }

        setIsDownloading(true);

        try {
            // 1. 从 CDN 下载 skill zip 文件
            const skillUrl = `${import.meta.env.VITE_CDN_HOST}/static/skills/${selectedSkill.download}`;
            console.log('下载 skill 文件:', skillUrl);

            const response = await fetch(skillUrl);
            if (!response.ok) {
                throw new Error(`下载失败: ${response.statusText}`);
            }

            const zipBlob = await response.blob();

            // 2. 解压 zip 文件
            const zip = await JSZip.loadAsync(zipBlob);

            // 打印 zip 中的所有文件结构
            console.log('ZIP 文件内容:');
            zip.forEach((relativePath, file) => {
                console.log(`  - ${relativePath}${file.dir ? ' (目录)' : ''}`);
            });

            // 3. 替换 .env 文件中的占位符
            // 首先尝试在根目录查找
            let envFile = zip.file('.env');

            // 如果根目录没有，递归查找所有 .env 文件
            if (!envFile) {
                const envFiles: string[] = [];
                zip.forEach((relativePath, file) => {
                    if (!file.dir && relativePath.endsWith('.env')) {
                        envFiles.push(relativePath);
                    }
                });

                if (envFiles.length > 0) {
                    console.log('找到以下 .env 文件:', envFiles);
                    // 使用第一个找到的 .env 文件
                    envFile = zip.file(envFiles[0]);
                }
            }

            if (envFile) {
                let envContent = await envFile.async('string');
                console.log('.env 原始内容:', envContent.substring(0, 200));

                // 替换占位符
                envContent = envContent.replace(/\{\{SUPABASE_TOKEN\}\}/g, token);
                envContent = envContent.replace(/\{\{WORKSPACE_ID\}\}/g, workspaceId);

                console.log('.env 替换后内容:', envContent.substring(0, 200));

                // 替换后的内容写回
                zip.file(envFile.name, envContent);
                console.log(`${envFile.name} 文件已更新`);
            } else {
                console.warn('未找到 .env 文件，跳过配置替换');
            }

            // 4. 重新打包并下载
            const content = await zip.generateAsync({ type: 'blob' });

            // 创建下载链接
            const url = URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.download = selectedSkill.download;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('Skill downloaded successfully');
        } catch (error) {
            console.error('Failed to download skill:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h1 className={styles.title}>Download Skill</h1>
                <p className={styles.description}>
                    Select a skill and download it with your current workspace configuration
                </p>

                <div className={styles.configSection}>
                    <h2 className={styles.sectionTitle}>Configuration</h2>

                    <div className={styles.infoGroup}>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Workspace ID:</span>
                            <span className={styles.infoValue}>
                                {workspaceId || 'Loading...'}
                            </span>
                        </div>

                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Token:</span>
                            <span className={styles.infoValue}>
                                {token && token.length > 20
                                    ? `${token.substring(0, 20)}...`
                                    : token || 'Loading...'
                                }
                            </span>
                        </div>
                    </div>

                    <h2 className={styles.sectionTitle}>Select Skill</h2>

                    {isLoadingSkills ? (
                        <p className={styles.loadingText}>Loading skills...</p>
                    ) : skills.length === 0 ? (
                        <p className={styles.loadingText}>No skills available</p>
                    ) : (
                        <div className={styles.skillsList}>
                            {skills.map((skill, index) => (
                                <div
                                    key={index}
                                    className={`${styles.skillCard} ${selectedSkill?.download === skill.download ? styles.selected : ''}`}
                                    onClick={() => setSelectedSkill(skill)}
                                >
                                    <div className={styles.skillName}>{skill.name}</div>
                                    {skill.description && (
                                        <div className={styles.skillDescription}>{skill.description}</div>
                                    )}
                                    {skill.version && (
                                        <div className={styles.skillVersion}>
                                            v
                                            {skill.version}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <Button
                        className={styles.downloadButton}
                        onClick={downloadSkill}
                        disabled={isDownloading || !isReady || !selectedSkill}
                        loading={isDownloading}
                        fullWidth
                    >
                        {isDownloading ? 'Preparing Download...' : 'Download Skill'}
                    </Button>

                    {!isReady && (
                        <p className={styles.loadingText}>
                            Loading configuration...
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Skill;
